import {
  ArrowRight,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Edit,
  GitMerge,
  ListNumber,
  Plus,
  PlusCircle,
  Question,
  Save,
  Trash,
  X,
} from "@mynaui/icons-react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  EmptyState,
  Field,
  FormGrid,
  IconButton,
  Input,
  Loader,
  PageHeader,
  Segmented,
  Select,
  Tag,
  TagTone,
} from "../../ui";
import { Shuffle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { AddAwardDTO, AddCategoryDTO, AddFeeDTO, AddPrizeDTO } from "../../types/tournament";
import { addCategory, getTournamentDetail } from "../../api/tournaments";
import CategoryScheduleField from "../../components/CategoryScheduleField";
import moment from "moment";
import "./styles.scss";
import {
  apiErrorMessage,
  getRankName,
  isAmericanoFormat,
  playersPerSideError,
  unitsPerSideFor,
} from "../../utils/methods";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { feeWindowStatus, FeeWindowStatus } from "../../utils/dates";
import { currencyOptions } from "../../constants/currencies";
import {
  BOTH_TEAM_MIN_SIZE_ERROR,
  BOTH_TEAM_MIX_ERROR,
  BOTH_TEAM_SIZE_ERROR,
  bothGenderError,
  bothGenderSummary,
  bothSettingsError,
  fixedTeamSize,
  hasCategoryGenderLimit,
  modeFlags,
  NO_CATEGORY_GENDER_LIMIT,
  RegistrationMode,
  registrationModeOf,
  storedTeamMinSize,
  teamMinSizeOptions,
} from "../../utils/registrationMode";

// A registration fee amount is valid when it's a real number >= 0. A 0 amount
// is allowed (free registration); only blank/NaN/negative amounts are invalid.
const isValidFeeAmount = (amount: unknown): boolean =>
  typeof amount === "number" && !Number.isNaN(amount) && amount >= 0;

const STEPS_LIST = [
  { title: "Category Details", description: "Give this category a name and some basic details" },
  {
    title: "Format",
    description: "How will this category play out? Fill out the details of the competition",
  },
  {
    title: "Teams & Players",
    description: "How many teams and players will be participating? What is the gender of this category?",
  },
  {
    title: "Prizes & Awards",
    description:
      "Add prizes for each final position. You can choose to add more prize categories (e.g. MVP of the tournament...etc.)",
  },
  {
    title: "Fees",
    description: "",
  },
];

const FEE_STATUS_TONE: Record<FeeWindowStatus["kind"], TagTone> = {
  active: "ok",
  upcoming: "info",
  ended: "danger",
  always: "neutral",
};

const GENDER_OPTIONS = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Mixed", value: "mixed" },
];

const REGISTRATION_MODES: { value: RegistrationMode; label: string }[] = [
  { value: "teams", label: "Teams" },
  { value: "individuals", label: "Individuals" },
  { value: "both", label: "Both" },
];

const TOURNAMENT_FORMATS = [
  { value: "League", label: "League", icon: <ListNumber size={32} /> },
  { value: "Knockout", label: "Knockout", icon: <GitMerge size={32} /> },
  { value: "Americano", label: "Americano", icon: <Shuffle size={32} /> },
  { value: "Mexicano", label: "Mexicano", icon: <Shuffle size={32} /> },
  { value: "Roulette", label: "Roulette", icon: <Shuffle size={32} /> },
  { value: "Custom", label: "Custom", icon: <Question size={32} /> },
];

const AddCategory = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const step = searchParams.get("step");
  const type = searchParams.get("type");
  const tournamentId = window.location.pathname.split("/")[3];

  const currentStep = parseInt(step!);
  const currentStepItem = currentStep > STEPS_LIST.length ? null : STEPS_LIST[step ? currentStep - 1 : 0];

  const [isCreated, setIsCreated] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [errors, setErrors] = useState<{ rank: number; field: string }[]>([]);
  const [feeErrors, setFeeErrors] = useState<{ id: string; field: string }[]>([]);
  const [awardErrors, setAwardErrors] = useState<{ id: string; field: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedGender, setSelectedGender] = useState<"mixed" | "male" | "female">("male");
  // Mixed Both: the admin opted in to whole-category caps (all teams and solo players).
  const [categoryCapsTicked, setCategoryCapsTicked] = useState(false);
  const [tournamentDates, setTournamentDates] = useState<{ startDate: string; endDate: string } | null>(null);

  const NAME_SUGGESTIONS_LIST =
    type === "beach-volleyball" || type === "beach-tennis"
    ? ["2v2 Men", "2v2 Women", "2v2 Mixed", "3v3 Mixed"]
    : ["Male", "Female", "Mixed"];

  const [category, setCategory] = useState<AddCategoryDTO>({
    endDate: "",
    maxMen: 100,
    maxPlayers: 100,
    maxWomen: 0,
    minMen: 0,
    minWomen: 0,
    name: "",
    spotsNum: 16,
    startDate: "",
    femaleBonusPoints: 0,
    femaleBonusCap: 0,
    isIncludedInRanking: false,
    allowIndividualRegistration: false,
    allowTeamRegistration: false,
    teamSize: null,
    teamMinSize: null,
    teamMaxMen: null,
    teamMaxWomen: null,
    prizes: [],
    fees: [],
    awards: [],
    customFormatBlocks: [],
    format: {
      type: "League",
      encounters: 1,
      lossPts: 0,
      winPts: 3,
      mode: "single",
      rankingCriteria: "total",
      thirdPlace: 1,
    },
  });

  const handleNext = async () => {
    if (isNextDisabled()) return;

    if (currentStep === STEPS_LIST.length) {
      setLoading(true);
      try {
        const res = await addCategory(tournamentId, {
          format: category.format.type,
          formatBlocks:
            category?.format?.type === "League" || category?.format?.type === "Knockout"
              ? [
                  {
                    id: "",
                    blockType: category.format.type,
                    orderIndex: 1,
                    leagueRules:
                      category.format.type === "League"
                        ? {
                            encounters: category.format?.encounters,
                            pointsForWin: category.format?.winPts,
                            pointsForDraw: 0,
                            pointsForLoss: category.format?.lossPts,
                            criteria: category.format?.rankingCriteria,
                          }
                        : undefined,
                    knockoutRules:
                      category.format.type === "Knockout"
                        ? {
                            mode: category.format?.mode,
                            hasThirdPlaceMatch: category.format?.thirdPlace === 1 ? true : false,
                          }
                        : undefined,
                    isRankingBlock: true,
                  },
                ]
              : isAmericanoFormat(category?.format?.type)
              ? [
                  {
                    id: "",
                    blockType: category.format.type,
                    orderIndex: 1,
                    isRankingBlock: true,
                    americanoRules: {
                      pointsPerMatch: category.format.pointsPerMatch ?? 24,
                      // Roulette: 0 = open-ended; the setup step may plan rounds later.
                      roundsCount: category.format.type === "Roulette" ? 0 : category.format.roundsCount ?? 4,
                      unitSize: category.format.unitSize ?? 1,
                      // Only Americano pairs by gender.
                      mixedPairing:
                        category.format.type === "Americano" && (category.format.unitSize ?? 1) === 1
                          ? category.format.mixedPairing ?? false
                          : false,
                      ...(category.format.type === "Roulette" && {
                        unitsPerSide: unitsPerSideFor(
                          category.format.playersPerSide ?? 2,
                          category.format.unitSize ?? 1
                        ),
                      }),
                    },
                  },
                ]
              : category?.format?.type === "League-Knockout" || category?.format?.type === "Groups-Knockout"
              ? [
                  {
                    id: "",
                    blockType: category.format.type === "League-Knockout" ? "League" : "Group",
                    orderIndex: 1,
                    leagueRules: {
                      encounters: category.format?.encounters,
                      pointsForWin: category.format?.winPts,
                      pointsForDraw: 0,
                      pointsForLoss: category.format?.lossPts,
                      criteria: category.format?.rankingCriteria,
                    },
                    isRankingBlock: false,
                  },
                  {
                    id: "",
                    blockType: "Knockout",
                    orderIndex: 2,
                    knockoutRules: {
                      mode: category.format?.mode,
                      hasThirdPlaceMatch: category.format?.thirdPlace === 1 ? true : false,
                    },
                    // The knockout decides the final placings.
                    isRankingBlock: true,
                  },
                ]
              : category.customFormatBlocks?.map((item, id) => ({
                  ...item,
                  orderIndex: id + 1,
                  ...(item.isRankingBlock !== undefined && { IsRankingBlock: item.isRankingBlock }),
                })),
          maxMen: category.maxMen,
          maxPlayers: category.maxPlayers,
          maxWomen: category.maxWomen,
          minMen: category.minMen,
          minWomen: category.minWomen,
          femaleBonusPoints: category.femaleBonusPoints,
          femaleBonusCap: category.femaleBonusCap,
          isIncludedInRanking: category.isIncludedInRanking,
          allowIndividualRegistration: category.allowIndividualRegistration,
          allowTeamRegistration: isBoth,
          // A format with fixed units sets the team size itself; it's stored too, so a later change to a
          // format without units still has one.
          teamSize: isBoth ? formatTeamSize ?? category.teamSize ?? null : null,
          // null = teams have exactly the team size
          teamMinSize: isBoth ? storedTeamMinSize(category.teamMinSize, bothTeamSize) : null,
          teamMaxMen: isBoth && selectedGender === "mixed" ? category.teamMaxMen ?? null : null,
          teamMaxWomen: isBoth && selectedGender === "mixed" ? category.teamMaxWomen ?? null : null,
          name: category.name,
          spotsNum: category.spotsNum,
          endDate: category.endDate !== "" ? moment(category.endDate).format("YYYY-MM-DDTHH:mm:ss") : undefined,
          startDate: category.startDate !== "" ? moment(category.startDate).format("YYYY-MM-DDTHH:mm:ss") : undefined,
          requireConsent: false,
          fees: category.fees.map((fee) => ({
            amount: fee.amount,
            currency: fee.currency,
            description: fee.description,
            isEnabled: true,
            perTeam: fee.perTeam,
            startDate: fee.startDate || null,
            endDate: fee.endDate || null,
          })),
          prizes: category.prizes.map((prize) => ({
            amount: prize.amount,
            currency: prize.currency,
            gift: prize.gift,
            rank: prize.rank,
            rankingPoints: prize.rankingPoints,
          })),
          awards: category.awards.map((award) => ({
            name: award.label,
            rankingpoints: award.rankingPoints,
            includedInVoting: true,
          })),
        });
        // The category endpoints can answer 200 with success: false; that's a refusal, not a create.
        if ((res as any)?.success === false) throw res;
        setCategoryId(res.data.id);
        setIsCreated(true);
      } catch (error: any) {
        console.error("ADD CATEGORY", error?.message);
        // Stay on this step so the organizer can fix it and try again.
        dispatch(
          setGeneralAlertMessage({
            type: "error",
            message: apiErrorMessage(error, "Couldn't create the category. Please try again."),
          })
        );
        return;
      } finally {
        setLoading(false);
      }
    }

    const urlParams = new URLSearchParams();
    urlParams.append("step", `${currentStep + 1}`);
    urlParams.append("reset", "true");

    const goTo = searchParams.get("goTo");
    const typeParam = searchParams.get("type");
    const nameParam = searchParams.get("name");

    if (typeParam) urlParams.append("type", typeParam);
    if (nameParam) urlParams.append("name", nameParam);
    if (goTo) urlParams.append("goTo", goTo);

    setSearchParams(urlParams);
  };

  const handleBack = () => {
    const urlParams = new URLSearchParams();
    urlParams.append("step", `${currentStep - 1}`);
    urlParams.append("reset", "true");

    const goTo = searchParams.get("goTo");
    const typeParam = searchParams.get("type");
    const nameParam = searchParams.get("name");

    if (typeParam) urlParams.append("type", typeParam);
    if (nameParam) urlParams.append("name", nameParam);
    if (goTo) urlParams.append("goTo", goTo);

    setSearchParams(urlParams);
  };

  const inputChangeHandler = (field: string, value: string | boolean) => {
    setCategory((prev) => ({ ...(prev as AddCategoryDTO), [field]: value }));
  };

  const prizeInputChangeHandler = (field: string, value: string | number, rank: number) => {
    setCategory((prev) => {
      const updatedPrizes = [...prev.prizes];
      const idx = updatedPrizes.findIndex((x) => x.rank === rank);
      if (idx !== -1) updatedPrizes[idx] = { ...updatedPrizes[idx], [field]: value };
      return { ...prev, prizes: updatedPrizes };
    });
  };

  const awardInputChangeHandler = (field: string, value: string | number | boolean, id: string) => {
    setCategory((prev) => {
      const updatedAwards = [...prev.awards];
      const idx = updatedAwards.findIndex((x) => x.id === id);
      if (idx !== -1) updatedAwards[idx] = { ...updatedAwards[idx], [field]: value };
      return { ...prev, awards: updatedAwards };
    });
  };

  const feeInputChangeHandler = (field: string, value: string | boolean | number | null, id: string) => {
    setCategory((prev) => {
      const updatedFees = [...prev.fees];
      const idx = updatedFees.findIndex((x) => x.id === id);
      if (idx !== -1)
        updatedFees[idx] = {
          ...updatedFees[idx],
          [field]: value,
        };

      return { ...prev, fees: updatedFees };
    });
  };

  // Roulette: players per side must be a whole number of units, 1-12 (spec 2026-10-14 §4.1).
  const rouletteSideError =
    category.format.type === "Roulette"
      ? playersPerSideError(category.format.playersPerSide ?? 2, category.format.unitSize ?? 1)
      : null;

  // Teams / Individuals / Both (spec 2026-10-29): Both is an individual category that also takes complete teams.
  const registrationMode = registrationModeOf(category.allowIndividualRegistration, category.allowTeamRegistration);
  const isBoth = registrationMode === "both";
  const formatTeamSize = fixedTeamSize(category.format.type, category.format.unitSize);
  const bothTeamSize = formatTeamSize ?? category.teamSize ?? null;
  const bothError = isBoth
    ? bothSettingsError(formatTeamSize, {
        teamSize: category.teamSize,
        teamMinSize: category.teamMinSize,
        teamMaxMen: selectedGender === "mixed" ? category.teamMaxMen : null,
        teamMaxWomen: selectedGender === "mixed" ? category.teamMaxWomen : null,
        fees: category.fees,
      })
    : null;
  // Size, minimum and mix errors sit under their own fields; the rest under the Registration choice.
  const registrationError =
    bothError === BOTH_TEAM_SIZE_ERROR || bothError === BOTH_TEAM_MIN_SIZE_ERROR || bothError === BOTH_TEAM_MIX_ERROR
      ? null
      : bothError;
  const teamMixLimits = Array.from({ length: Math.max(formatTeamSize ?? category.teamSize ?? 0, 0) + 1 }, (_, n) => n);
  // Min. players per team: 2 up to the team size; no minimum = the size (teams must be complete).
  const teamMinSizeValue = category.teamMinSize ?? bothTeamSize;
  // A Both category must let someone in: 0 men and 0 women is refused (under the gender choice).
  const genderError = isBoth ? bothGenderError(category.maxMen, category.maxWomen) : null;
  // Mixed Both: whole-category caps are an opt-in; a cap below the Mixed default keeps them on.
  const categoryCapsOn = categoryCapsTicked || hasCategoryGenderLimit(category.maxMen, category.maxWomen);
  const toggleCategoryCaps = (on: boolean) => {
    setCategoryCapsTicked(on);
    // Off = the Mixed default: no limit across the category.
    if (!on)
      setCategory((prev) => ({
        ...prev,
        maxMen: NO_CATEGORY_GENDER_LIMIT,
        maxWomen: NO_CATEGORY_GENDER_LIMIT,
        maxPlayers: NO_CATEGORY_GENDER_LIMIT * 2,
      }));
  };

  const isNextDisabled = () => {
    if (currentStep === 1) return !category?.name;
    if (currentStep === 2 && isAmericanoFormat(category.format.type)) {
      return (
        !category.format.pointsPerMatch ||
        category.format.pointsPerMatch < 1 ||
        // Roulette's rounds are open-ended (0) unless the setup step plans some.
        (category.format.type !== "Roulette" && (!category.format.roundsCount || category.format.roundsCount < 1)) ||
        !category.format.unitSize ||
        category.format.unitSize < 1 ||
        rouletteSideError !== null
      );
    }
    if (currentStep === 3) {
      if (category.allowIndividualRegistration) {
        // For individual registration, we only need to check that spotsNum is set (and Both's team settings)
        return !category.spotsNum || category.spotsNum < 1 || bothError !== null || genderError !== null;
      }
      // For team registration, validate team restrictions
      return (
        (selectedGender === "male" && !category.minMen) ||
        (selectedGender === "female" && !category.minWomen) ||
        (selectedGender === "mixed" && (!category.minMen || !category.minWomen)) ||
        category.minMen > category.maxMen ||
        category.minWomen > category.maxWomen ||
        category.maxPlayers < category.minMen + category.minWomen
      );
    }
    if (currentStep === 4)
      return (
        category.prizes.findIndex(
          (x) => (x.prizeOption === "cash" && !x.amount) || (x.prizeOption === "other" && !x.gift)
        ) !== -1 ||
        category.prizes.findIndex((x) => !x.added) !== -1 ||
        category.prizes.length === 0
      );
    if (currentStep === 5)
      return (
        category.fees.findIndex((x) => !isValidFeeAmount(x.amount)) !== -1 ||
        category.fees.findIndex((x) => !x.added) !== -1 ||
        category.fees.length === 0
      );
  };

  // Award Functions
  const addAwardHandler = () => {
    setCategory((prev) => ({
      ...prev,
      awards: [
        ...prev.awards,
        {
          added: false,
          edit: false,
          label: "",
          id: Date.now().toString(),
          rankingpoints: 1,
          includedInVoting: true,
        },
      ],
    }));
  };

  const editAwardHandler = (id: string) => {
    setCategory((prev) => {
      const updatedAwards = [...prev.awards];
      const idx = updatedAwards.findIndex((x) => x.id === id);
      if (idx !== -1) updatedAwards[idx] = { ...updatedAwards[idx], edit: true };
      return { ...prev, awards: updatedAwards };
    });
  };

  const saveAwardHandler = (item: AddAwardDTO) => {
    if (item.label) {
      setCategory((prev) => {
        const updatedAwards = [...prev.awards];
        const idx = updatedAwards.findIndex((x) => x.id === item.id);
        if (idx !== -1)
          updatedAwards[idx] = {
            ...updatedAwards[idx],
            added: true,
            edit: false,
          };

        return { ...prev, awards: updatedAwards };
      });
    } else {
      setAwardErrors((prev) => [...prev, { field: "label", id: item.id }]);
    }
  };

  const deleteAwardHandler = (id: string) => {
    setCategory((prev) => {
      let updatedAwards = [...prev.awards];
      updatedAwards = updatedAwards.filter((x) => x.id !== id);
      return { ...prev, awards: updatedAwards };
    });
  };

  const cancelAwardEditHandler = (id: string) => {
    setCategory((prev) => {
      const updatedAwards = [...prev.awards];
      const idx = updatedAwards.findIndex((x) => x.id === id);
      if (idx !== -1)
        updatedAwards[idx] = {
          ...updatedAwards[idx],
          added: true,
          edit: false,
        };

      return { ...prev, awards: updatedAwards };
    });
  };

  // Prize Functions
  const addPrizeHandler = () => {
    setCategory((prev) => {
      const nextRank = prev.prizes.length > 0 ? Math.max(...prev.prizes.map((prize) => prize.rank)) + 1 : 1;
      const newPrize = {
        amount: undefined,
        currency: "EGP",
        gift: "",
        rank: nextRank,
        prizeOption: "cash",
        added: false,
        edit: false,
      };

      // Set default medal values for 1st, 2nd, 3rd place when "other" is selected
      if (nextRank <= 3) {
        newPrize.prizeOption = "other";
        newPrize.gift =
          nextRank === 1 ? "Gold Medal" : nextRank === 2 ? "Silver Medal" : nextRank === 3 ? "Bronze Medal" : "";
      }

      return {
        ...prev,
        prizes: [...prev.prizes, newPrize],
      };
    });
  };

  const savePrizeHandler = (item: AddPrizeDTO) => {
    if ((item.prizeOption === "cash" && item.amount) || (item.prizeOption === "other" && item.gift)) {
      setCategory((prev) => {
        const updatedPrizes = [...prev.prizes];
        const idx = updatedPrizes.findIndex((x) => x.rank === item.rank);
        if (idx !== -1)
          updatedPrizes[idx] = {
            ...updatedPrizes[idx],
            added: true,
            edit: false,
          };

        return { ...prev, prizes: updatedPrizes };
      });
    } else {
      if (item.prizeOption === "cash") setErrors((prev) => [...prev, { field: "amount", rank: item.rank }]);
      if (item.prizeOption === "other") setErrors((prev) => [...prev, { field: "gift", rank: item.rank }]);
    }
  };

  const editPrizeHandler = (rank: number) => {
    setCategory((prev) => {
      const updatedPrizes = [...prev.prizes];
      const idx = updatedPrizes.findIndex((x) => x.rank === rank);
      if (idx !== -1) updatedPrizes[idx] = { ...updatedPrizes[idx], edit: true };
      return { ...prev, prizes: updatedPrizes };
    });
  };

  const deletePrizeHandler = (rank: number) => {
    setCategory((prev) => {
      let updatedPrizes = [...prev.prizes];
      updatedPrizes = updatedPrizes.filter((x) => x.rank !== rank);

      // Reorder remaining prizes to maintain sequential ranking
      updatedPrizes = updatedPrizes.map((prize, index) => {
        const newRank = index + 1;
        return {
          ...prize,
          rank: newRank,
          // Update medal defaults for 1st, 2nd, 3rd place
          gift:
            prize.prizeOption === "other" && newRank <= 3
              ? newRank === 1
                ? "Gold Medal"
                : newRank === 2
                ? "Silver Medal"
                : "Bronze Medal"
              : prize.gift,
        };
      });

      return { ...prev, prizes: updatedPrizes };
    });
  };

  const cancelPrizeEditHandler = (rank: number) => {
    setCategory((prev) => {
      const updatedPrizes = [...prev.prizes];
      const idx = updatedPrizes.findIndex((x) => x.rank === rank);
      if (idx !== -1)
        updatedPrizes[idx] = {
          ...updatedPrizes[idx],
          added: true,
          edit: false,
        };

      return { ...prev, prizes: updatedPrizes };
    });
  };

  const handlePrizeOptionChange = (rank: number, option: string) => {
    setCategory((prev) => {
      const updatedPrizes = [...prev.prizes];
      const idx = updatedPrizes.findIndex((x) => x.rank === rank);
      if (idx !== -1) {
        updatedPrizes[idx] = {
          ...updatedPrizes[idx],
          prizeOption: option,
          // Set default medal values for 1st, 2nd, 3rd place when switching to "other"
          // Clear gift when switching to cash for medal positions
          gift:
            option === "other" && rank <= 3
              ? rank === 1
                ? "Gold Medal"
                : rank === 2
                ? "Silver Medal"
                : "Bronze Medal"
              : option === "cash" && rank <= 3
              ? ""
              : updatedPrizes[idx].gift,
        };
      }
      return { ...prev, prizes: updatedPrizes };
    });
  };

  // Fee Functions
  const addFeeHandler = () => {
    setCategory((prev) => ({
      ...prev,
      fees: [
        ...prev.fees,
        {
          id: Date.now().toString(),
          added: false,
          amount: undefined,
          currency: "EGP",
          description: "Registration Fee",
          perTeam: false,
          edit: false,
          startDate: null,
          endDate: null,
        },
      ],
    }));
  };

  const editFeeHandler = (id: string) => {
    setCategory((prev) => {
      const updatedFees = [...prev.fees];
      const idx = updatedFees.findIndex((x) => x.id === id);
      if (idx !== -1) updatedFees[idx] = { ...updatedFees[idx], edit: true };
      return { ...prev, fees: updatedFees };
    });
  };

  const saveFeeHandler = (item: AddFeeDTO) => {
    if (
      isValidFeeAmount(item.amount) &&
      item.description &&
      category.fees.findIndex((x) => x.description === item.description && x.id !== item.id) === -1
    ) {
      setCategory((prev) => {
        const updatedFees = [...prev.fees];
        const idx = updatedFees.findIndex((x) => x.id === item.id);
        if (idx !== -1)
          updatedFees[idx] = {
            ...updatedFees[idx],
            added: true,
            edit: false,
          };

        return { ...prev, fees: updatedFees };
      });
    } else {
      if (!isValidFeeAmount(item.amount)) setFeeErrors((prev) => [...prev, { field: "amount", id: item.id }]);
      else setFeeErrors((prev) => [...prev, { field: "description", id: item.id }]);
    }
  };

  const deleteFeeHandler = (id: string) => {
    setCategory((prev) => {
      let updatedFees = [...prev.fees];
      updatedFees = updatedFees.filter((x) => x.id !== id);
      return { ...prev, fees: updatedFees };
    });
  };

  const cancelEditHandler = (id: string) => {
    setCategory((prev) => {
      const updatedFees = [...prev.fees];
      const idx = updatedFees.findIndex((x) => x.id === id);
      if (idx !== -1)
        updatedFees[idx] = {
          ...updatedFees[idx],
          added: true,
          edit: false,
        };

      return { ...prev, fees: updatedFees };
    });
  };

  const onAddCustomBlock = () => {
    setCategory((prev) => {
      let updatedBlocks = [...prev?.customFormatBlocks!];

      updatedBlocks.push({
        id: Date.now().toString(),
        blockType: "",
        aliasName: "",
        orderIndex: updatedBlocks.length + 1,
        collapsed: false,
        isRankingBlock: false,
        leagueRules: {
          criteria: "total",
          encounters: 1,
          pointsForDraw: 0,
          pointsForLoss: 0,
          pointsForWin: 3,
        },
        knockoutRules: {
          hasThirdPlaceMatch: true,
          mode: "single",
        },
      });

      return { ...prev, customFormatBlocks: updatedBlocks };
    });
  };

  const handleDeleteCustomBlock = (id: string) => {
    setCategory((prev) => {
      let updatedBlocks = [...prev?.customFormatBlocks!];
      return { ...prev, customFormatBlocks: updatedBlocks.filter((x) => x.id !== id) };
    });
  };

  const customBlockInputChangeHandler = (id: string, field: string, value: string | boolean) => {
    setCategory((prev) => {
      let updatedBlocks = [...prev?.customFormatBlocks!];

      const idx = updatedBlocks.findIndex((x) => x.id === id);
      updatedBlocks[idx] = {
        ...updatedBlocks[idx],
        [field]: value,
      };

      if (field == "blockType") {
        updatedBlocks[idx] = {
          ...updatedBlocks[idx],
          leagueRules:
            value === "League" || value === "Group"
              ? { criteria: "total", encounters: 1, pointsForDraw: 0, pointsForLoss: 0, pointsForWin: 3 }
              : undefined,
          knockoutRules: value === "Knockout" ? { hasThirdPlaceMatch: true, mode: "single" } : undefined,
        };
      }

      return { ...prev, customFormatBlocks: updatedBlocks };
    });
  };

  const customBlockLeagueInputChangeHandler = (id: string, field: string, value: string | boolean) => {
    setCategory((prev) => {
      let updatedBlocks = [...prev?.customFormatBlocks!];

      const idx = updatedBlocks.findIndex((x) => x.id === id);
      updatedBlocks[idx] = {
        ...updatedBlocks[idx],
        leagueRules: {
          ...updatedBlocks[idx].leagueRules,
          [field]: value,
        },
      };

      return { ...prev, customFormatBlocks: updatedBlocks };
    });
  };

  const customBlockKnockoutInputChangeHandler = (id: string, field: string, value: string | boolean) => {
    setCategory((prev) => {
      let updatedBlocks = [...prev?.customFormatBlocks!];

      const idx = updatedBlocks.findIndex((x) => x.id === id);
      updatedBlocks[idx] = {
        ...updatedBlocks[idx],
        knockoutRules: {
          ...updatedBlocks[idx].knockoutRules,
          [field]: value,
        },
      };

      return { ...prev, customFormatBlocks: updatedBlocks };
    });
  };

  useEffect(() => {
    document.title = "Create New Category";

    const urlParams = new URLSearchParams();

    const goTo = searchParams.get("goTo");
    const typeParam = searchParams.get("type");
    const nameParam = searchParams.get("name");

    if (goTo) urlParams.append("goTo", goTo);
    if (typeParam) urlParams.append("type", typeParam);
    if (nameParam) urlParams.append("name", nameParam);

    urlParams.append("step", "1");

    if (currentStep > 1 && !category?.name) {
      urlParams.append("step", "1");
      urlParams.append("reset", "true");
    }

    setSearchParams(urlParams);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getTournamentDetail(tournamentId).then((res) => {
      if (!cancelled) {
        setTournamentDates({ startDate: res.data.startDate, endDate: res.data.endDate });
      }
    }).catch((e) => {
      console.error("Failed to load tournament dates", e);
    });
    return () => { cancelled = true; };
  }, [tournamentId]);

  // Teams / Individuals / Both (the old "Allow Individual Registration" tick, plus Both)
  const changeRegistrationMode = (mode: RegistrationMode) => {
    const flags = modeFlags(mode);
    setCategory((prev) => ({
      ...prev,
      ...flags,
      // Turning individual registration on: a mixed category takes up to maxMen + maxWomen players.
      ...(flags.allowIndividualRegistration &&
        !prev.allowIndividualRegistration &&
        selectedGender === "mixed" && { maxPlayers: (prev.maxMen || 0) + (prev.maxWomen || 0) }),
      // Fees are per player when a category takes teams and individuals.
      ...(mode === "both" && { fees: prev.fees.map((fee) => ({ ...fee, perTeam: false })) }),
    }));
  };

  return (
    <div className="add-category-page">
      {currentStepItem && (
        <PageHeader title={currentStepItem?.title} subtitle={currentStepItem?.description || undefined} />
      )}

      <div className="content">
        {loading ? (
          <Card>
            <Loader />
          </Card>
        ) : isCreated ? (
          <Card className="category-created-container">
            <EmptyState
              icon={<CheckCircle />}
              title="Category Created!"
              description="This category is all set! You can make changes before registration if needed"
              action={
                <>
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/dashboard/tournaments/${tournamentId}/${categoryId}`, { replace: true })}
                  >
                    Go to Tournament Category
                  </Button>
                  <Button variant="ghost" onClick={() => navigate(`/dashboard/tournaments/${tournamentId}`)}>
                    Back to Tournament
                  </Button>
                </>
              }
            />
          </Card>
        ) : currentStep === 1 ? (
          <Card>
            <div className="category-name-section">
              <div className="name-container">
                <Field label="Name" required>
                  {(fieldId) => (
                    <Input
                      id={fieldId}
                      value={category?.name}
                      onChange={(evt) => inputChangeHandler("name", evt.target.value)}
                      placeholder="Enter category name"
                    />
                  )}
                </Field>
                <div className="suggestions-list">
                  <p className="suggestion-title">Suggestions: </p>
                  {NAME_SUGGESTIONS_LIST.map((item, id) => (
                    <Button key={id} variant="tint" size="sm" onClick={() => inputChangeHandler("name", item)}>
                      {item}
                    </Button>
                  ))}
                </div>
              </div>
              {tournamentDates && (
                <CategoryScheduleField
                  tournamentStartDate={tournamentDates.startDate}
                  tournamentEndDate={tournamentDates.endDate}
                  value={{ startDate: category.startDate || null, endDate: category.endDate || null }}
                  onChange={(next) =>
                    setCategory((prev) => ({
                      ...prev,
                      startDate: next.startDate || "",
                      endDate: next.endDate || "",
                    }))
                  }
                />
              )}
              <Checkbox
                id="isIncludedInRanking"
                checked={category?.isIncludedInRanking ?? false}
                onChange={(checked) => inputChangeHandler("isIncludedInRanking", checked)}
                label="Include in Ranking"
              />
            </div>
          </Card>
        ) : currentStep === 2 ? (
          <div className="format-section">
            <Card>
              <div className="formats-list" role="radiogroup">
                {TOURNAMENT_FORMATS.map((item, id) => (
                  <button
                    type="button"
                    role="radio"
                    aria-checked={category.format.type === item.value}
                    key={id}
                    className={`option-card tournament-format ${category.format.type === item.value ? "selected" : ""}`}
                    onClick={() =>
                      setCategory((prev) => ({
                        ...prev,
                        format: {
                          ...prev.format,
                          type: item.value,
                          ...(isAmericanoFormat(item.value) && {
                            pointsPerMatch: prev.format.pointsPerMatch ?? 24,
                            roundsCount: prev.format.roundsCount ?? 4,
                            unitSize: prev.format.unitSize ?? 1,
                            mixedPairing: prev.format.mixedPairing ?? false,
                            playersPerSide: prev.format.playersPerSide ?? 2,
                          }),
                        },
                      }))
                    }
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </Card>
            {(category.format.type.includes("League") || category.format.type.includes("Groups")) && (
              <Card title={`${category.format.type.includes("League") ? "League" : "Group"} Rules`}>
                <FormGrid columns={4}>
                  <Field label="Encounters">
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        min={1}
                        value={category.format.encounters}
                        onChange={(e) =>
                          setCategory((prev) => ({
                            ...prev,
                            format: { ...prev.format, encounters: parseInt(e.target.value) },
                          }))
                        }
                      />
                    )}
                  </Field>
                  <Field label="Points for Win">
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        min={0}
                        value={category.format.winPts}
                        onChange={(e) =>
                          setCategory((prev) => ({
                            ...prev,
                            format: { ...prev.format, winPts: parseInt(e.target.value) },
                          }))
                        }
                      />
                    )}
                  </Field>
                  <Field label="Points for Loss">
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        value={category.format.lossPts}
                        onChange={(e) =>
                          setCategory((prev) => ({
                            ...prev,
                            format: { ...prev.format, lossPts: parseInt(e.target.value) },
                          }))
                        }
                      />
                    )}
                  </Field>
                  <Field label="Ranking Criteria">
                    {(fieldId) => (
                      <Select
                        id={fieldId}
                        block
                        value={category.format.rankingCriteria ?? ""}
                        onChange={(e) =>
                          setCategory((prev) => ({
                            ...prev,
                            format: { ...prev.format, rankingCriteria: e.target.value },
                          }))
                        }
                      >
                        <option value="total">Total</option>
                        <option value="direct">Direct Matches</option>
                      </Select>
                    )}
                  </Field>
                </FormGrid>
              </Card>
            )}
            {category.format.type.includes("Knockout") && (
              <Card title="Knockout Rules">
                <FormGrid columns={2}>
                  <Field label="Mode">
                    {(fieldId) => (
                      <Select
                        id={fieldId}
                        block
                        value={category.format.mode ?? ""}
                        onChange={(e) =>
                          setCategory((prev) => ({ ...prev, format: { ...prev.format, mode: e.target.value } }))
                        }
                      >
                        <option value="single">Single elimination</option>
                        <option value="double">Double elimination</option>
                      </Select>
                    )}
                  </Field>
                  <Field label="3rd Place Match">
                    {(fieldId) => (
                      <Select
                        id={fieldId}
                        block
                        value={category.format.thirdPlace ?? ""}
                        onChange={(e) =>
                          setCategory((prev) => ({
                            ...prev,
                            format: { ...prev.format, thirdPlace: parseInt(e.target.value) },
                          }))
                        }
                      >
                        <option value={1}>Yes</option>
                        <option value={0}>No</option>
                      </Select>
                    )}
                  </Field>
                </FormGrid>
              </Card>
            )}
            {isAmericanoFormat(category.format.type) && (
              <Card title={`${category.format.type} Rules`}>
                <FormGrid columns={3}>
                  <Field
                    label="Points per Match"
                    hint="Rounds are configured when creating the schedule (they depend on the number of players and courts)."
                  >
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        min={1}
                        value={category.format.pointsPerMatch ?? 24}
                        onChange={(e) =>
                          setCategory((prev) => ({
                            ...prev,
                            format: { ...prev.format, pointsPerMatch: parseInt(e.target.value) },
                          }))
                        }
                      />
                    )}
                  </Field>
                  <Field
                    label="Unit Size"
                    hint={
                      category.format.type === "Roulette"
                        ? "1 = individual players, 2+ = fixed teams formed at the venue."
                        : undefined
                    }
                  >
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        min={1}
                        value={category.format.unitSize ?? 1}
                        onChange={(e) => {
                          const unitSize = parseInt(e.target.value);
                          setCategory((prev) => ({
                            ...prev,
                            format: {
                              ...prev.format,
                              unitSize,
                              mixedPairing: unitSize === 1 ? prev.format.mixedPairing : false,
                            },
                          }));
                        }}
                      />
                    )}
                  </Field>
                  {category.format.type === "Roulette" && (
                    <Field
                      label="Players per Side"
                      error={rouletteSideError}
                      hint="A multiple of the unit size, up to 12."
                    >
                      {(fieldId) => (
                        <Input
                          id={fieldId}
                          type="number"
                          min={1}
                          max={12}
                          invalid={!!rouletteSideError}
                          value={Number.isFinite(category.format.playersPerSide) ? category.format.playersPerSide : ""}
                          onChange={(e) =>
                            setCategory((prev) => ({
                              ...prev,
                              format: { ...prev.format, playersPerSide: parseInt(e.target.value) },
                            }))
                          }
                        />
                      )}
                    </Field>
                  )}
                  {/* Only Americano pairs by gender; Mexicano and Roulette never do (spec 2026-09-29, 2026-10-14). */}
                  {category.format.type === "Americano" && (
                    <Field label="Mixed Pairing">
                      {(fieldId) => (
                        <Select
                          id={fieldId}
                          block
                          disabled={(category.format.unitSize ?? 1) !== 1}
                          value={category.format.mixedPairing ? 1 : 0}
                          onChange={(e) =>
                            setCategory((prev) => ({
                              ...prev,
                              format: { ...prev.format, mixedPairing: Number(e.target.value) === 1 },
                            }))
                          }
                        >
                          <option value={1}>Yes</option>
                          <option value={0}>No</option>
                        </Select>
                      )}
                    </Field>
                  )}
                </FormGrid>
              </Card>
            )}
            {category?.format?.type.includes("Custom") && (
              <Card>
                <div className="custom-format-container">
                  {category?.customFormatBlocks?.map((block, id) => (
                    <div className="custom-block-item" key={id}>
                      <div
                        className="header-container"
                        role="button"
                        tabIndex={0}
                        aria-expanded={!block.collapsed}
                        onClick={() => customBlockInputChangeHandler(block.id, "collapsed", !block.collapsed)}
                        onKeyDown={(e) => {
                          if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
                          e.preventDefault();
                          customBlockInputChangeHandler(block.id, "collapsed", !block.collapsed);
                        }}
                      >
                        <p className="custom-block-title">
                          Block {id + 1}
                          {block.collapsed && block.blockType ? ` - ${block.aliasName || block.blockType}` : ""}
                        </p>
                        <div className="row">
                          <IconButton
                            label="Delete"
                            variant="danger"
                            size="sm"
                            icon={<Trash size={16} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCustomBlock(block.id);
                            }}
                          />
                          {block.collapsed ? <ChevronDown /> : <ChevronUp />}
                        </div>
                      </div>
                      {block.collapsed ? null : (
                        <div className="custom-block-body">
                          <Select
                            block
                            aria-label="Block type"
                            value={block.blockType}
                            onChange={(e) => customBlockInputChangeHandler(block.id, "blockType", e.target.value)}
                          >
                            <option value="" disabled>
                              -- Select a block type --
                            </option>
                            <option value="League">League</option>
                            <option value="Group">Group</option>
                            <option value="Knockout">Knockout</option>
                          </Select>
                          <Field label="Name">
                            {(fieldId) => (
                              <Input
                                id={fieldId}
                                type="text"
                                placeholder="Enter format name"
                                value={block.aliasName || ""}
                                onChange={(e) => customBlockInputChangeHandler(block.id, "aliasName", e.target.value)}
                              />
                            )}
                          </Field>
                          <Checkbox
                            id={`isRankingBlock-${block.id}`}
                            checked={block.isRankingBlock ?? false}
                            onChange={(checked) => customBlockInputChangeHandler(block.id, "isRankingBlock", checked)}
                            label="Ranking Block"
                          />
                          {block.blockType === "Knockout" ? (
                            <FormGrid columns={2}>
                              <Field label="Mode">
                                {(fieldId) => (
                                  <Select
                                    id={fieldId}
                                    block
                                    onChange={(e) =>
                                      customBlockKnockoutInputChangeHandler(block.id, "mode", e.target.value)
                                    }
                                    value={block.knockoutRules?.mode ?? ""}
                                  >
                                    <option value={"single"}>Single elimination</option>
                                    <option value={"double"}>Double elimination</option>
                                  </Select>
                                )}
                              </Field>
                              <Field label="3rd Place Match">
                                {(fieldId) => (
                                  <Select
                                    id={fieldId}
                                    block
                                    onChange={(e) =>
                                      customBlockKnockoutInputChangeHandler(
                                        block.id,
                                        "hasThirdPlaceMatch",
                                        Number(e.target.value) === 1 ? true : false
                                      )
                                    }
                                    value={block.knockoutRules?.hasThirdPlaceMatch ? 1 : 0}
                                  >
                                    <option value={1}>Yes</option>
                                    <option value={0}>No</option>
                                  </Select>
                                )}
                              </Field>
                            </FormGrid>
                          ) : block.blockType === "League" || block.blockType === "Group" ? (
                            <FormGrid columns={4}>
                              <Field label="Points for Win">
                                {(fieldId) => (
                                  <Input
                                    id={fieldId}
                                    type="number"
                                    value={block.leagueRules?.pointsForWin}
                                    onChange={(e) =>
                                      customBlockLeagueInputChangeHandler(block.id, "pointsForWin", e.target.value)
                                    }
                                  />
                                )}
                              </Field>
                              <Field label="Points for Loss">
                                {(fieldId) => (
                                  <Input
                                    id={fieldId}
                                    type="number"
                                    value={block.leagueRules?.pointsForLoss}
                                    onChange={(e) =>
                                      customBlockLeagueInputChangeHandler(block.id, "pointsForLoss", e.target.value)
                                    }
                                  />
                                )}
                              </Field>
                              <Field label="Encounters">
                                {(fieldId) => (
                                  <Input
                                    id={fieldId}
                                    type="number"
                                    value={block.leagueRules?.encounters}
                                    onChange={(e) =>
                                      customBlockLeagueInputChangeHandler(block.id, "encounters", e.target.value)
                                    }
                                  />
                                )}
                              </Field>
                              <Field label="Ranking Criteria">
                                {(fieldId) => (
                                  <Select
                                    id={fieldId}
                                    block
                                    onChange={(e) =>
                                      customBlockLeagueInputChangeHandler(block.id, "criteria", e.target.value)
                                    }
                                    value={block.leagueRules?.criteria ?? ""}
                                  >
                                    <option value={"total"}>Total Points</option>
                                    <option value={"direct"}>Direct Matches</option>
                                  </Select>
                                )}
                              </Field>
                            </FormGrid>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ))}
                  <Button variant="tint" icon={<Plus size={18} />} onClick={onAddCustomBlock} className="custom-add-btn">
                    Add Block
                  </Button>
                </div>
              </Card>
            )}
          </div>
        ) : currentStep === 3 ? (
          <div className="team-restrictions-section">
            <Card>
              <div className="general-section">
                <FormGrid columns={2}>
                  <Field label={category.allowIndividualRegistration ? "Num. of Players" : "Num. of Teams"}>
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        min={1}
                        value={category.spotsNum}
                        onChange={(e) => setCategory((prev) => ({ ...prev, spotsNum: parseInt(e.target.value) }))}
                      />
                    )}
                  </Field>
                  {!category.allowIndividualRegistration && (
                    <Field label="Max players per team">
                      {(fieldId) => (
                        <Select
                          id={fieldId}
                          block
                          value={category.maxPlayers}
                          onChange={(e) =>
                            setCategory((prev) => ({
                              ...prev,
                              maxPlayers: parseInt(e.target.value),
                            }))
                          }
                        >
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                          <option value="5">5</option>
                          <option value="6">6</option>
                          <option value="7">7</option>
                          <option value="8">8</option>
                          <option value="9">9</option>
                          <option value="10">10</option>
                          <option value="100">Unlimited</option>
                        </Select>
                      )}
                    </Field>
                  )}
                  {isBoth && (
                    <Field
                      label="Players per team"
                      error={bothError === BOTH_TEAM_SIZE_ERROR ? bothError : undefined}
                    >
                      {(fieldId) =>
                        formatTeamSize !== null ? (
                          <p className="team-size-fixed">Teams of {formatTeamSize} (from the format)</p>
                        ) : (
                          <Input
                            id={fieldId}
                            type="number"
                            min={2}
                            value={category.teamSize ?? ""}
                            invalid={bothError === BOTH_TEAM_SIZE_ERROR}
                            onChange={(e) => {
                              const teamSize = parseInt(e.target.value);
                              setCategory((prev) => ({ ...prev, teamSize: Number.isNaN(teamSize) ? null : teamSize }));
                            }}
                          />
                        )
                      }
                    </Field>
                  )}
                  {isBoth && (
                    <Field
                      label="Min. players per team"
                      error={bothError === BOTH_TEAM_MIN_SIZE_ERROR ? bothError : undefined}
                      hint={
                        teamMinSizeValue != null && bothTeamSize != null && teamMinSizeValue < bothTeamSize
                          ? "Fill short teams with waiting players before they play."
                          : undefined
                      }
                    >
                      {(fieldId) => (
                        <Select
                          id={fieldId}
                          block
                          disabled={teamMinSizeOptions(bothTeamSize).length === 0}
                          invalid={bothError === BOTH_TEAM_MIN_SIZE_ERROR}
                          value={teamMinSizeValue ?? ""}
                          onChange={(e) => {
                            const teamMinSize = parseInt(e.target.value);
                            // The team size itself means exactly that many: saved as no minimum.
                            setCategory((prev) => ({
                              ...prev,
                              teamMinSize: Number.isNaN(teamMinSize) ? null : teamMinSize,
                            }));
                          }}
                        >
                          {teamMinSizeOptions(bothTeamSize).map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                  )}
                </FormGrid>

                <Field label="Registration" error={registrationError}>
                  <Segmented
                    ariaLabel="Registration"
                    options={REGISTRATION_MODES.map((option) => ({
                      ...option,
                      // Players play alone in a unit-size-1 format: nothing to pair.
                      disabled: option.value === "both" && formatTeamSize === 1,
                    }))}
                    value={registrationMode}
                    onChange={changeRegistrationMode}
                  />
                </Field>

                <Field error={genderError}>
                  <div className="gender-options" role="radiogroup">
                    {GENDER_OPTIONS.map((item, id) => (
                      <button
                        type="button"
                        role="radio"
                        aria-checked={item.value === selectedGender}
                        key={id}
                        className={`option-card gender-card ${item.value === selectedGender ? "selected" : ""}`}
                        onClick={() => {
                          setSelectedGender(item.value as "mixed" | "male" | "female");
                          // One side 0 makes a single-gender category; the other side must stay open
                          // (never 0 men and 0 women).
                          if (item.value === "male") {
                            setCategory((prev) => {
                              const maxMen = prev.maxMen || 100;
                              return {
                                ...prev,
                                maxWomen: 0,
                                minWomen: 0,
                                maxMen,
                                maxPlayers: prev.allowIndividualRegistration ? maxMen : prev.maxPlayers,
                              };
                            });
                          }
                          if (item.value === "female") {
                            setCategory((prev) => {
                              const maxWomen = prev.maxWomen || 100;
                              return {
                                ...prev,
                                maxMen: 0,
                                minMen: 0,
                                maxWomen,
                                maxPlayers: prev.allowIndividualRegistration ? maxWomen : prev.maxPlayers,
                              };
                            });
                          }
                          if (item.value === "mixed") {
                            // Mixed opens with no category-wide limit (Both: the box starts unticked).
                            setCategoryCapsTicked(false);
                            setCategory((prev) => ({
                              ...prev,
                              maxMen: 100,
                              maxWomen: 100,
                              maxPlayers: prev.allowIndividualRegistration ? 200 : prev.maxPlayers,
                            }));
                          }
                        }}
                      >
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
            </Card>

            {isBoth && selectedGender === "mixed" && (
              <>
                <Card title="Each team">
                  <FormGrid columns={2}>
                    <Field
                      label="Max. men per team"
                      error={bothError === BOTH_TEAM_MIX_ERROR ? bothError : undefined}
                    >
                      {(fieldId) => (
                        <Select
                          id={fieldId}
                          block
                          invalid={bothError === BOTH_TEAM_MIX_ERROR}
                          value={category.teamMaxMen ?? ""}
                          onChange={(e) =>
                            setCategory((prev) => ({
                              ...prev,
                              teamMaxMen: e.target.value === "" ? null : parseInt(e.target.value),
                            }))
                          }
                        >
                          <option value="">No limit</option>
                          {teamMixLimits.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                    <Field label="Max. women per team">
                      {(fieldId) => (
                        <Select
                          id={fieldId}
                          block
                          invalid={bothError === BOTH_TEAM_MIX_ERROR}
                          value={category.teamMaxWomen ?? ""}
                          onChange={(e) =>
                            setCategory((prev) => ({
                              ...prev,
                              teamMaxWomen: e.target.value === "" ? null : parseInt(e.target.value),
                            }))
                          }
                        >
                          <option value="">No limit</option>
                          {teamMixLimits.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                  </FormGrid>
                </Card>

                <Card title="Whole category">
                  <div className="category-caps">
                    <Checkbox
                      label="Also limit men and women across the whole category (all teams and solo players)"
                      checked={categoryCapsOn}
                      onChange={toggleCategoryCaps}
                    />
                    {categoryCapsOn && (
                      <FormGrid columns={2}>
                        <Field label="Max. men in total">
                          {(fieldId) => (
                            <Input
                              id={fieldId}
                              type="number"
                              min={0}
                              value={category.maxMen}
                              onChange={(e) => {
                                const maxMen = parseInt(e.target.value) || 0;
                                setCategory((prev) => ({
                                  ...prev,
                                  maxMen,
                                  maxPlayers: maxMen + (prev.maxWomen || 0),
                                }));
                              }}
                            />
                          )}
                        </Field>
                        <Field label="Max. women in total">
                          {(fieldId) => (
                            <Input
                              id={fieldId}
                              type="number"
                              min={0}
                              value={category.maxWomen}
                              onChange={(e) => {
                                const maxWomen = parseInt(e.target.value) || 0;
                                setCategory((prev) => ({
                                  ...prev,
                                  maxWomen,
                                  maxPlayers: (prev.maxMen || 0) + maxWomen,
                                }));
                              }}
                            />
                          )}
                        </Field>
                      </FormGrid>
                    )}
                    <p className="gender-summary">
                      {bothGenderSummary({
                        teamMaxMen: category.teamMaxMen,
                        teamMaxWomen: category.teamMaxWomen,
                        limitCategory: categoryCapsOn,
                        maxMen: category.maxMen,
                        maxWomen: category.maxWomen,
                      })}
                    </p>
                  </div>
                </Card>
              </>
            )}

            {category.allowIndividualRegistration && !isBoth && selectedGender === "mixed" && (
              <Card title="Gender Distribution">
                <FormGrid columns={2}>
                  <Field label="Max. # of Males">
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        min={0}
                        value={category.maxMen}
                        onChange={(e) => {
                          const maxMen = parseInt(e.target.value) || 0;
                          setCategory((prev) => ({
                            ...prev,
                            maxMen,
                            maxPlayers: maxMen + (prev.maxWomen || 0),
                          }));
                        }}
                      />
                    )}
                  </Field>
                  <Field label="Max. # of Females">
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        min={0}
                        value={category.maxWomen}
                        onChange={(e) => {
                          const maxWomen = parseInt(e.target.value) || 0;
                          setCategory((prev) => ({
                            ...prev,
                            maxWomen,
                            maxPlayers: (prev.maxMen || 0) + maxWomen,
                          }));
                        }}
                      />
                    )}
                  </Field>
                </FormGrid>
              </Card>
            )}

            {!category.allowIndividualRegistration && (
              <Card title="Team Restrictions">
                <div className="restrictions">
                  {selectedGender !== "female" && (
                    <FormGrid columns={2}>
                      <Field label="Min. # of Males per Team">
                        {(fieldId) => (
                          <Input
                            id={fieldId}
                            type="number"
                            min={2}
                            value={category.minMen}
                            onChange={(e) => setCategory((prev) => ({ ...prev, minMen: parseInt(e.target.value) }))}
                          />
                        )}
                      </Field>
                      <Field label="Max. # of Males per Team">
                        {(fieldId) => (
                          <Select
                            id={fieldId}
                            block
                            value={category.maxMen}
                            onChange={(e) =>
                              setCategory((prev) => ({
                                ...prev,
                                maxMen: parseInt(e.target.value),
                              }))
                            }
                          >
                            <option value="0">0</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                            <option value="5">5</option>
                            <option value="6">6</option>
                            <option value="7">7</option>
                            <option value="8">8</option>
                            <option value="9">9</option>
                            <option value="10">10</option>
                            <option value="100">Unlimited</option>
                          </Select>
                        )}
                      </Field>
                    </FormGrid>
                  )}

                  {selectedGender !== "male" && (
                    <FormGrid columns={2}>
                      <Field label="Min. # of Females per Team">
                        {(fieldId) => (
                          <Input
                            id={fieldId}
                            type="number"
                            min={2}
                            value={category.minWomen}
                            onChange={(e) => setCategory((prev) => ({ ...prev, minWomen: parseInt(e.target.value) }))}
                          />
                        )}
                      </Field>
                      <Field label="Max # of Females per Team">
                        {(fieldId) => (
                          <Select
                            id={fieldId}
                            block
                            value={category.maxWomen}
                            onChange={(e) =>
                              setCategory((prev) => ({
                                ...prev,
                                maxWomen: parseInt(e.target.value),
                              }))
                            }
                          >
                            <option value="0">0</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                            <option value="5">5</option>
                            <option value="6">6</option>
                            <option value="7">7</option>
                            <option value="8">8</option>
                            <option value="9">9</option>
                            <option value="10">10</option>
                            <option value="100">Unlimited</option>
                          </Select>
                        )}
                      </Field>
                    </FormGrid>
                  )}
                </div>
              </Card>
            )}

            {!category.allowIndividualRegistration && selectedGender !== "male" && (
              <Card title="Bonus Points">
                <FormGrid columns={2}>
                  <Field label="Female Bonus Points">
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        min={0}
                        value={category.femaleBonusPoints}
                        onChange={(e) =>
                          setCategory((prev) => ({ ...prev, femaleBonusPoints: parseInt(e.target.value) || 0 }))
                        }
                      />
                    )}
                  </Field>
                  <Field label="Female Bonus Cap">
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        type="number"
                        min={0}
                        value={category.femaleBonusCap}
                        onChange={(e) =>
                          setCategory((prev) => ({ ...prev, femaleBonusCap: parseInt(e.target.value) || 0 }))
                        }
                      />
                    )}
                  </Field>
                </FormGrid>
              </Card>
            )}
          </div>
        ) : currentStep === 4 ? (
          <div className="prizes-awards-section">
            <Card
              title="Prizes"
              subtitle='💡 Tip: 1st, 2nd, and 3rd place automatically default to medals when "Other" is selected'
            >
              <div className="entry-list">
                {category.prizes
                  .sort((a, b) => (a.rank > b.rank ? 1 : -1))
                  .map((item, id) => {
                    console.log("Add Category Prize data:", item);
                    return (
                      <div
                        className={`entry-item prize-item ${
                          item.prizeOption === "other" && item.rank <= 3 ? "medal-prize" : ""
                        }`}
                        key={id}
                      >
                        <div className="rank-container">
                          {item.added && (
                            <span
                              className={`rank-circle ${
                                item.rank === 1 ? "gold" : item.rank === 2 ? "silver" : item.rank === 3 ? "bronze" : ""
                              }`}
                            >
                              {item.rank}
                            </span>
                          )}
                          <p className="rank">
                            {getRankName(item.rank)} Place
                            {item.prizeOption === "other" && item.rank <= 3 && (
                              <span className="medal-indicator">
                                {item.rank === 1 ? "🏅" : item.rank === 2 ? "🥈" : "🥉"}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="entry-data">
                          {item.added && !item.edit ? (
                            <>
                              <div className="prize-display">
                                <p className="detail-text">
                                  {item.prizeOption === "cash" ? "Cash Prize" : item.rank <= 3 ? "Medal" : "Other"}
                                  {" - "}
                                  {item.prizeOption === "cash"
                                    ? `${item.amount?.toLocaleString()} ${item.currency}`
                                    : item.gift}
                                </p>
                                {item.rankingPoints !== undefined && item.rankingPoints !== null && (
                                  <p className="ranking-points">Ranking Points: {item.rankingPoints}</p>
                                )}
                              </div>
                              <div className="row">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  icon={<Edit size={16} />}
                                  onClick={() => editPrizeHandler(item.rank)}
                                >
                                  Edit
                                </Button>
                                <IconButton
                                  label="Delete"
                                  variant="danger"
                                  icon={<Trash size={18} />}
                                  onClick={() => deletePrizeHandler(item.rank)}
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <Select
                                className="narrow-select"
                                aria-label="Prize type"
                                value={item.prizeOption ?? ""}
                                onChange={(e) => handlePrizeOptionChange(item.rank, e.target.value)}
                              >
                                <option value={"cash"}>Cash</option>
                                <option value={"other"}>Other</option>
                              </Select>
                              {item.prizeOption === "cash" ? (
                                <>
                                  <Select
                                    className="narrow-select"
                                    aria-label="Currency"
                                    value={item.currency}
                                    onChange={(e) => prizeInputChangeHandler("currency", e.target.value, item.rank)}
                                  >
                                    {currencyOptions(item.currency).map((code) => (
                                      <option key={code} value={code}>
                                        {code}
                                      </option>
                                    ))}
                                  </Select>
                                  <Input
                                    type="number"
                                    block={false}
                                    placeholder="Amount"
                                    aria-label="Amount"
                                    value={item.amount}
                                    invalid={!!errors.find((x) => x.rank === item.rank && x.field === "amount")}
                                    onChange={(e) => {
                                      setErrors((prev) =>
                                        prev.filter((x) => !(x.rank === item.rank && x.field === "amount"))
                                      );
                                      prizeInputChangeHandler("amount", parseInt(e.target.value), item.rank);
                                    }}
                                  />
                                </>
                              ) : (
                                <Input
                                  type="text"
                                  block={false}
                                  placeholder={item.rank <= 3 ? "Medal Type (e.g., Gold Medal)" : "Gift"}
                                  aria-label={item.rank <= 3 ? "Medal Type (e.g., Gold Medal)" : "Gift"}
                                  value={item.gift}
                                  onChange={(e) => prizeInputChangeHandler("gift", e.target.value, item.rank)}
                                  invalid={!!errors.find((x) => x.rank === item.rank && x.field === "gift")}
                                />
                              )}
                              <Input
                                type="number"
                                block={false}
                                placeholder="Ranking Points"
                                aria-label="Ranking Points"
                                value={item.rankingPoints || ""}
                                onChange={(e) =>
                                  prizeInputChangeHandler("rankingPoints", parseInt(e.target.value) || 0, item.rank)
                                }
                              />
                              <div className="row">
                                <Button
                                  icon={item.edit ? <Save size={18} /> : <PlusCircle size={18} />}
                                  onClick={() => savePrizeHandler(item)}
                                >
                                  {item.edit ? "Save" : "Add"}
                                </Button>
                                <IconButton
                                  label={item.edit ? "Cancel" : "Delete"}
                                  icon={<X size={18} />}
                                  onClick={() =>
                                    item.edit ? cancelPrizeEditHandler(item.rank) : deletePrizeHandler(item.rank)
                                  }
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                <Button variant="tint" icon={<Plus size={18} />} onClick={addPrizeHandler} className="add-btn">
                  Add Prize
                </Button>
              </div>
            </Card>

            <Card title="Awards">
              <div className="entry-list">
                {category.awards.length > 0 &&
                  category.awards.map((item, id) => (
                    <div className="entry-item award-item" key={id}>
                      {item.added && !item.edit ? (
                        <>
                          <p className="entry-title">{item.label}</p>
                          <div className="row">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<Edit size={16} />}
                              onClick={() => editAwardHandler(item.id)}
                            >
                              Edit
                            </Button>
                            <IconButton
                              label="Delete"
                              variant="danger"
                              icon={<Trash size={18} />}
                              onClick={() => deleteAwardHandler(item.id)}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="name-points-row">
                            <Input
                              value={item.label}
                              placeholder="Award Description (ex: MVP)"
                              aria-label="Award Description (ex: MVP)"
                              invalid={!!awardErrors.find((x) => x.field === "label" && x.id === item.id)}
                              onChange={(e) => {
                                setAwardErrors((prev) => prev.filter((x) => !(x.id === item.id && x.field === "label")));
                                awardInputChangeHandler("label", e.target.value, item.id);
                              }}
                            />
                            <Input
                              type="number"
                              block={false}
                              className="points-input"
                              placeholder="Points"
                              aria-label="Points"
                              value={item.rankingPoints || ""}
                              onChange={(e) =>
                                awardInputChangeHandler("rankingPoints", parseInt(e.target.value) || 0, item.id)
                              }
                            />
                          </div>
                          <div className="row">
                            <Button
                              icon={item.edit ? <Save size={18} /> : <PlusCircle size={18} />}
                              onClick={() => saveAwardHandler(item)}
                            >
                              {item.edit ? "Save" : "Add"}
                            </Button>
                            <IconButton
                              label={item.edit ? "Cancel" : "Delete"}
                              icon={<X size={18} />}
                              onClick={() =>
                                item.edit ? cancelAwardEditHandler(item.id) : deleteAwardHandler(item.id)
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                <Button variant="tint" icon={<Plus size={18} />} onClick={addAwardHandler} className="add-btn">
                  Add Award
                </Button>
              </div>
            </Card>
          </div>
        ) : currentStep === 5 ? (
          <Card>
            <div className="fees-section entry-list">
              {isBoth && <Alert tone="info">Fees are per player when a category takes teams and individuals.</Alert>}
              {category.fees.map((item, id) => (
                <div className="entry-item fee-container" key={id}>
                  {item.added && !item.edit ? (
                    <p className="entry-title fee-title">{item.description}</p>
                  ) : (
                    <Input
                      type="text"
                      block={false}
                      placeholder="Fee Description"
                      aria-label="Fee Description"
                      className="description"
                      invalid={!!feeErrors.find((x) => x.id === item.id && x.field === "description")}
                      value={item.description}
                      onChange={(e) => {
                        feeInputChangeHandler("description", e.target.value, item.id);
                        setFeeErrors((prev) => prev.filter((x) => !(x.id === item.id && x.field === "description")));
                      }}
                    />
                  )}
                  <div className="entry-data">
                    {item.added && !item.edit ? (
                      <>
                        <p className="detail-text">
                          {item?.amount?.toLocaleString()} {item.currency} {item.perTeam ? "per team" : "per player"}
                        </p>
                        {(() => {
                          const s = feeWindowStatus({ startDate: item.startDate, endDate: item.endDate });
                          return <Tag tone={FEE_STATUS_TONE[s.kind]}>{s.label}</Tag>;
                        })()}
                        <div className="row">
                          <Button variant="ghost" size="sm" icon={<Edit size={16} />} onClick={() => editFeeHandler(item.id)}>
                            Edit
                          </Button>
                          <IconButton
                            label="Delete"
                            variant="danger"
                            icon={<Trash size={18} />}
                            onClick={() => deleteFeeHandler(item.id)}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <Select
                          className="narrow-select"
                          aria-label="Fee applies to"
                          disabled={isBoth}
                          value={item.perTeam && !isBoth ? "team" : "player"}
                          onChange={(e) =>
                            feeInputChangeHandler("perTeam", e.target.value === "team" ? true : false, item.id)
                          }
                        >
                          <option value="team">Per Team</option>
                          <option value="player">Per Player</option>
                        </Select>
                        <Select
                          className="narrow-select"
                          aria-label="Currency"
                          value={item.currency}
                          onChange={(e) => feeInputChangeHandler("currency", e.target.value, item.id)}
                        >
                          {currencyOptions(item.currency).map((code) => (
                            <option key={code} value={code}>
                              {code}
                            </option>
                          ))}
                        </Select>
                        <Input
                          type="number"
                          block={false}
                          placeholder="Amount"
                          aria-label="Amount"
                          value={item.amount}
                          invalid={!!feeErrors.find((x) => x.id === item.id && x.field === "amount")}
                          onChange={(e) => {
                            setFeeErrors((prev) => prev.filter((x) => !(x.id === item.id && x.field === "amount")));
                            feeInputChangeHandler("amount", parseInt(e.target.value), item.id);
                          }}
                        />
                        <div className="fee-window-row">
                          <Field label="Available from" className="field-container">
                            {(fieldId) => (
                              <DatePicker
                                id={fieldId}
                                selected={item.startDate ? new Date(item.startDate) : null}
                                onChange={(d) =>
                                  feeInputChangeHandler(
                                    "startDate",
                                    d ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : "",
                                    item.id
                                  )
                                }
                                mode="datetime"
                                timeIntervals={15}
                                dateFormat="dd/MM/yyyy HH:mm"
                                placeholderText="Always available"
                                isClearable
                              />
                            )}
                          </Field>
                          <Field label="Available until" className="field-container">
                            {(fieldId) => (
                              <DatePicker
                                id={fieldId}
                                selected={item.endDate ? new Date(item.endDate) : null}
                                onChange={(d) =>
                                  feeInputChangeHandler(
                                    "endDate",
                                    d ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : "",
                                    item.id
                                  )
                                }
                                mode="datetime"
                                timeIntervals={15}
                                dateFormat="dd/MM/yyyy HH:mm"
                                placeholderText="No end"
                                minDate={item.startDate ? new Date(item.startDate) : undefined}
                                isClearable
                              />
                            )}
                          </Field>
                        </div>
                        <div className="row">
                          <Button
                            icon={item.edit ? <Save size={18} /> : <PlusCircle size={18} />}
                            onClick={() => saveFeeHandler(item)}
                          >
                            {item.edit ? "Save" : "Add"}
                          </Button>
                          <IconButton
                            label={item.edit ? "Cancel" : "Delete"}
                            icon={<X size={18} />}
                            onClick={() => (item.edit ? cancelEditHandler(item.id) : deleteFeeHandler(item.id))}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
              <Button variant="tint" icon={<Plus size={18} />} onClick={addFeeHandler} className="add-btn">
                Add Fee
              </Button>
            </div>
          </Card>
        ) : null}
      </div>

      {currentStepItem && (
        <div className="footer">
          <div className="buttons-container">
            {currentStep !== 1 && (
              <Button variant="ghost" onClick={handleBack}>
                Back
              </Button>
            )}
            <Button
              variant="primary"
              disabled={!!isNextDisabled()}
              onClick={handleNext}
              iconRight={STEPS_LIST.length !== currentStep ? <ArrowRight size={18} /> : undefined}
            >
              {STEPS_LIST.length === currentStep ? "Finish" : "Next"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCategory;
